import axios from 'axios';
// Remove: import sample data or mock utils
// import auth and config if needed for headers and baseURL

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export async function getResilienceFactors() {
  const res = await axios.get(`${API_BASE}/impact/resilience/factors`);
  return res.data;
}

export async function getResilienceScores(fyYear) {
  const params = new URLSearchParams();
  if (fyYear) params.append('fyYear', fyYear);
  const query = params.toString();
  const res = await axios.get(`${API_BASE}/impact/resilience/scores${query ? `?${query}` : ''}`);
  return res.data;
}

// Add other API functions as needed for filters, etc.

export const getFilterCountries = async () => {
  const response = await axios.get(`${API_BASE}/impact/filters/countries`);
  return response.data;
};

export const getFilterProvinces = async (countryId) => {
  const response = await axios.get(`${API_BASE}/impact/filters/provinces/${countryId}`);
  return response.data;
};

export const getFilterMunicipalities = async (provinceId) => {
  const response = await axios.get(`${API_BASE}/impact/filters/municipalities/${provinceId}`);
  return response.data;
};

export const getFilterSites = async () => {
  const response = await axios.get(`${API_BASE}/impact/filters/sites`);
  return response.data;
};

export const getImpactHistory = async (options = {}) => {
  const {
    siteId,
    countryId,
    factorId,
    from,
    to,
    include_initial = true,
    page = 1,
    per_page = 50,
  } = options;

  const params = new URLSearchParams();
  if (siteId) params.append('site_id', siteId);
  if (countryId) params.append('country_id', countryId);
  if (factorId) params.append('factor_id', factorId);
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  if (include_initial === false) params.append('include_initial', 'false');
  if (page) params.append('page', page);
  if (per_page) params.append('per_page', per_page);

  const url = `${API_BASE}/impact/history${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await axios.get(url);
  return response.data;
};

export const getCompoundVariables = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/compound-variables/${siteId}`);
  return response.data;
};

export const getCompoundVariablesDetailed = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/compound-variables-detailed/${siteId}`);
  return response.data;
};

export const getClimateAdaptiveCapacity = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/climate-adaptive-capacity/${siteId}`);
  return response.data;
};

export const getCollectiveAction = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/collective-action/${siteId}`);
  return response.data;
};

export const getCollectiveActionComponents = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/collective-action-components/${siteId}`);
  return response.data;
};

export const getFinancialSufficiency = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/financial-sufficiency/${siteId}`);
  return response.data;
};

export const getFoodWorry = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/food-worry/${siteId}`);
  return response.data;
};

export const getBiodiversity = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/biodiversity/${siteId}`);
  return response.data;
};

export const getCpueBreakdown = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/cpue-breakdown/${siteId}`);
  return response.data;
};

export const getPolicyScores = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/policy-scores/by-site/${siteId}`);
  return response.data;
};

export const getEquityGender = async (siteId) => {
  const response = await axios.get(`${API_BASE}/impact/equity-gender/${siteId}`);
  return response.data;
};

export const getForecastScores = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.countryId) queryParams.append('countryId', params.countryId);
  if (params.provinceId) queryParams.append('provinceId', params.provinceId);
  if (params.municipalityId) queryParams.append('municipalityId', params.municipalityId);
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page);
  if (params.per_page) queryParams.append('per_page', params.per_page);
  
  const url = `${API_BASE}/impact/forecast/scores${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await axios.get(url);
  return response.data;
};

export const getForecastStats = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.countryId) queryParams.append('countryId', params.countryId);
  if (params.provinceId) queryParams.append('provinceId', params.provinceId);
  if (params.municipalityId) queryParams.append('municipalityId', params.municipalityId);
  if (params.search) queryParams.append('search', params.search);

  const url = `${API_BASE}/impact/forecast/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await axios.get(url);
  return response.data;
};

export const getForecastGlobalStats = async () => {
  const response = await axios.get(`${API_BASE}/impact/forecast/global-stats`);
  return response.data;
};

export const updateForecastScores = async (siteId, factorScores) => {
  const response = await axios.post(`${API_BASE}/impact/forecast/update`, {
    siteId,
    factorScores
  });
  return response.data;
};

export const getAIRecommendation = async (siteId, factorId) => {
  const response = await axios.get(`${API_BASE}/impact/ai-recommendation/${siteId}/${factorId}`);
  return response.data;
};

export const getCountrySummary = async (countryId, forceRegenerate = false) => {
  const url = forceRegenerate 
    ? `${API_BASE}/impact/country-summary/${countryId}?regenerate=true`
    : `${API_BASE}/impact/country-summary/${countryId}`;
  const response = await axios.get(url);
  return response.data;
};

export const getGlobalSummary = async (forceRegenerate = false) => {
  const url = forceRegenerate 
    ? `${API_BASE}/impact/global-summary?regenerate=true`
    : `${API_BASE}/impact/global-summary`;
  const response = await axios.get(url);
  return response.data;
};

const downloadCsv = async (url, filename) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to export CSV');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

export const exportScoresCSV = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.countryId) queryParams.append('countryId', params.countryId);
  if (params.provinceId) queryParams.append('provinceId', params.provinceId);
  if (params.municipalityId) queryParams.append('municipalityId', params.municipalityId);
  if (params.activeStatus) queryParams.append('activeStatus', params.activeStatus);
  if (params.fyYear) queryParams.append('fyYear', params.fyYear);
  
  const url = `${API_BASE}/impact/export/scores${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const dateStr = new Date().toISOString().split('T')[0];
  const fyPart = params.fyYear ? `_FY${params.fyYear}` : '';
  await downloadCsv(url, `resilience_scores_export${fyPart}_${dateStr}.csv`);
};

export const exportCompoundVariablesCSV = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.countryId) queryParams.append('countryId', params.countryId);
  if (params.provinceId) queryParams.append('provinceId', params.provinceId);
  if (params.municipalityId) queryParams.append('municipalityId', params.municipalityId);
  if (params.activeStatus) queryParams.append('activeStatus', params.activeStatus);

  const url = `${API_BASE}/impact/export/compound-variables${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const dateStr = new Date().toISOString().split('T')[0];
  await downloadCsv(url, `compound_variables_export_${dateStr}.csv`);
}; 